import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { format, parseISO } from 'date-fns';
import {
  FileText, Search, Filter, Plus, ChevronRight, DollarSign,
  Clock, CheckCircle, AlertCircle, XCircle, Send, Eye,
  Download, MoreVertical, Calendar, User, Home, CreditCard,
  Loader, Check, Square, CheckSquare, Bell, X, Mail, Heart
} from 'lucide-react';
import { invoiceStatuses, formatCurrency, isInvoiceOverdue, getDaysOverdue } from '../utils/invoiceData';
import { createStripeCustomer, getStripeInvoice, findStripeInvoiceByInternalId } from '../utils/stripeApi';
import { sendEmail } from '../utils/notificationsApi';
import './Invoices.css';

function Invoices() {
  const { state, updateInvoice, updateJob, updateClient, logClientCommunication } = useApp();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [showFilters, setShowFilters] = useState(false);

  // Selection state
  const [selectedInvoices, setSelectedInvoices] = useState(new Set());
  const [isSending, setIsSending] = useState(false);
  const [sendingProgress, setSendingProgress] = useState({ current: 0, total: 0 });
  const [sendResults, setSendResults] = useState(null);

  // Batch reminder state
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [reminderSelections, setReminderSelections] = useState(new Set());
  const [isSendingReminders, setIsSendingReminders] = useState(false);
  const [reminderProgress, setReminderProgress] = useState({ current: 0, total: 0 });
  const [reminderResults, setReminderResults] = useState(null);

  // Stripe sync state
  const [isSyncingStripe, setIsSyncingStripe] = useState(false);
  const [stripeSyncProgress, setStripeSyncProgress] = useState({ current: 0, total: 0 });
  const [stripeSyncResults, setStripeSyncResults] = useState(null);

  // Get invoices with overdue status updated
  const invoicesWithStatus = useMemo(() => {
    return state.invoices.map(inv => {
      // Check if sent invoice is overdue
      if ((inv.status === 'sent' || inv.status === 'pending') && isInvoiceOverdue(inv)) {
        return { ...inv, displayStatus: 'overdue' };
      }
      return { ...inv, displayStatus: inv.status };
    });
  }, [state.invoices]);

  // Filter invoices
  const filteredInvoices = useMemo(() => {
    let filtered = invoicesWithStatus;

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(inv =>
        inv.clientName?.toLowerCase().includes(query) ||
        inv.invoiceNumber?.toLowerCase().includes(query) ||
        inv.propertyAddress?.toLowerCase().includes(query)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      if (statusFilter === 'deposit_pending') {
        // Show invoices with deposits requested but not yet paid
        filtered = filtered.filter(inv => inv.requestDeposit && !inv.depositPaid && inv.displayStatus !== 'paid' && inv.displayStatus !== 'cancelled');
      } else if (statusFilter === 'pending') {
        // When filtering by 'pending', also show 'sent' invoices
        filtered = filtered.filter(inv => inv.displayStatus === 'pending' || inv.displayStatus === 'sent');
      } else {
        filtered = filtered.filter(inv => inv.displayStatus === statusFilter);
      }
    }

    // Date filter
    if (dateFilter !== 'all') {
      const today = new Date();
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const startOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      
      filtered = filtered.filter(inv => {
        const invDate = parseISO(inv.invoiceDate);
        switch (dateFilter) {
          case 'this_month':
            return invDate >= startOfMonth;
          case 'last_month':
            return invDate >= startOfLastMonth && invDate < startOfMonth;
          default:
            return true;
        }
      });
    }

    // Sort
    const sorted = [...filtered];
    switch (sortBy) {
      case 'oldest':
        sorted.sort((a, b) => new Date(a.invoiceDate) - new Date(b.invoiceDate));
        break;
      case 'due_soon':
        sorted.sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
        break;
      case 'amount_high':
        sorted.sort((a, b) => (b.total || 0) - (a.total || 0));
        break;
      case 'amount_low':
        sorted.sort((a, b) => (a.total || 0) - (b.total || 0));
        break;
      case 'client':
        sorted.sort((a, b) => (a.clientName || '').localeCompare(b.clientName || ''));
        break;
      case 'newest':
      default:
        sorted.sort((a, b) => new Date(b.invoiceDate) - new Date(a.invoiceDate));
    }
    return sorted;
  }, [invoicesWithStatus, searchQuery, statusFilter, dateFilter, sortBy]);

  // Get draft invoices
  const draftInvoices = useMemo(() => 
    invoicesWithStatus.filter(inv => inv.status === 'draft'),
    [invoicesWithStatus]
  );

  // Calculate summary stats
  const stats = useMemo(() => {
    const all = invoicesWithStatus;
    const draftInvs = all.filter(i => i.displayStatus === 'draft');
    const awaitingInvs = all.filter(i => i.displayStatus === 'pending' || i.displayStatus === 'sent');
    const overdueInvs = all.filter(i => i.displayStatus === 'overdue');
    return {
      total: all.length,
      draft: draftInvs.length,
      draftTotal: draftInvs.reduce((sum, i) => sum + (i.total || 0), 0),
      sent: all.filter(i => i.displayStatus === 'sent').length,
      pending: awaitingInvs.length, // Combined for "awaiting payment"
      pendingTotal: awaitingInvs.reduce((sum, i) => sum + (i.balance || 0), 0),
      overdue: overdueInvs.length,
      overdueTotal: overdueInvs.reduce((sum, i) => sum + (i.balance || 0), 0),
      paid: all.filter(i => i.displayStatus === 'paid').length,
      depositsPending: all.filter(i => i.requestDeposit && !i.depositPaid && i.displayStatus !== 'paid' && i.displayStatus !== 'cancelled').length,
      totalOutstanding: [...awaitingInvs, ...overdueInvs].reduce((sum, i) => sum + (i.balance || 0), 0),
      totalPaidThisMonth: all
        .filter(i => {
          if (i.displayStatus !== 'paid') return false;
          const paidDate = i.paidAt ? parseISO(i.paidAt) : null;
          if (!paidDate) return false;
          const startOfMonth = new Date();
          startOfMonth.setDate(1);
          startOfMonth.setHours(0, 0, 0, 0);
          return paidDate >= startOfMonth;
        })
        .reduce((sum, i) => sum + i.amountPaid, 0)
    };
  }, [invoicesWithStatus]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'draft': return <FileText size={16} />;
      case 'sent': return <Send size={16} />;
      case 'pending': return <Clock size={16} />;
      case 'paid': return <CheckCircle size={16} />;
      case 'overdue': return <AlertCircle size={16} />;
      case 'cancelled': return <XCircle size={16} />;
      default: return <FileText size={16} />;
    }
  };

  // Toggle invoice selection
  const toggleSelection = (invoiceId, e) => {
    e.stopPropagation();
    setSelectedInvoices(prev => {
      const next = new Set(prev);
      if (next.has(invoiceId)) {
        next.delete(invoiceId);
      } else {
        next.add(invoiceId);
      }
      return next;
    });
  };

  // Select all drafts
  const selectAllDrafts = () => {
    const draftIds = draftInvoices.map(inv => inv.id);
    setSelectedInvoices(new Set(draftIds));
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedInvoices(new Set());
  };

  // Send single invoice via portal (branded email with portal link)
  // Stripe invoice is NOT created here — it gets created when the client
  // opens the portal, selects a tip, and clicks "Pay Now"
  const sendInvoiceViaPortal = async (invoice) => {
    console.log('=== SENDING INVOICE VIA PORTAL ===');
    console.log('Invoice:', invoice.invoiceNumber);

    const client = state.clients.find(c => c.id === invoice.clientId);

    if (!client) {
      throw new Error('Client not found');
    }

    // Use accounting email if set, otherwise fall back to main email
    const invoiceEmail = client.accountingEmail || client.email;

    if (!invoiceEmail) {
      throw new Error(`Client ${client.firstName} ${client.lastName} has no email address`);
    }

    // Step 1: Ensure client has Stripe customer ID (needed later when they pay via portal)
    let stripeCustomerId = client.stripeCustomerId;

    if (stripeCustomerId && stripeCustomerId.includes('test')) {
      console.log('Old test mode customer ID detected, will create new one');
      stripeCustomerId = null;
    }

    if (!stripeCustomerId) {
      const customerEmail = client.accountingEmail || client.email;
      console.log('Creating Stripe customer for:', customerEmail);
      try {
        const customerResult = await createStripeCustomer({
          name: `${client.firstName} ${client.lastName}`,
          email: customerEmail,
          phone: client.phone,
          clientId: client.id
        });

        stripeCustomerId = customerResult.customerId;
        await updateClient(client.id, { stripeCustomerId });
      } catch (err) {
        console.error('Failed to create Stripe customer:', err);
        throw new Error(`Failed to create Stripe customer: ${err.message}`);
      }
    }

    // Step 2: Generate a unique payment token for direct pay link (no login required)
    const paymentToken = invoice.paymentToken || `pay_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
    const payLink = `${window.location.origin}/pay/${paymentToken}`;
    const invoiceLogId = `log_${Date.now()}_${invoice.id}`;

    const emailResult = await sendEmail('invoice_sent', {
      clientId: client.id,
      commLogId: invoiceLogId,
      clientEmail: invoiceEmail,
      clientName: client.firstName,
      invoiceNumber: invoice.invoiceNumber,
      amount: formatCurrency(invoice.total),
      dueDate: format(parseISO(invoice.dueDate), 'MMMM d, yyyy'),
      portalLink: payLink,
      isDeposit: false
    });

    if (!emailResult.success) {
      throw new Error(emailResult.error || 'Failed to send invoice email via SendGrid');
    }

    // Step 3: Log communication
    await logClientCommunication(client.id, {
      id: invoiceLogId,
      type: 'email',
      category: 'invoice',
      subject: `Invoice ${invoice.invoiceNumber} Sent`,
      recipient: invoiceEmail,
      status: 'sent',
      details: {
        invoiceId: invoice.id,
        invoiceNumber: invoice.invoiceNumber,
        amount: invoice.total,
        batchSend: true
      }
    });

    // Step 4: Update invoice status with payment token for direct pay link
    await updateInvoice(invoice.id, {
      status: 'sent',
      sentAt: new Date().toISOString(),
      sentVia: 'email',
      paymentToken
    });

    console.log('Invoice sent via portal (Stripe invoice will be created when client pays)');
    return { success: true, invoiceId: invoice.id };
  };

  // Send selected invoices
  const handleSendSelected = async () => {
    if (selectedInvoices.size === 0) return;

    setIsSending(true);
    setSendingProgress({ current: 0, total: selectedInvoices.size });
    
    const results = { success: [], failed: [] };
    const invoicesToSend = invoicesWithStatus.filter(inv => selectedInvoices.has(inv.id));

    for (let i = 0; i < invoicesToSend.length; i++) {
      const invoice = invoicesToSend[i];
      setSendingProgress({ current: i + 1, total: invoicesToSend.length });

      try {
        await sendInvoiceViaPortal(invoice);
        results.success.push(invoice);
      } catch (error) {
        console.error(`Failed to send invoice ${invoice.invoiceNumber}:`, error);
        results.failed.push({ invoice, error: error.message });
      }
    }

    setIsSending(false);
    setSendResults(results);
    setSelectedInvoices(new Set());

    // Show alert with details if there were failures
    if (results.failed.length > 0) {
      const errorMessages = results.failed.map(f => `${f.invoice.invoiceNumber}: ${f.error}`).join('\n');
      alert(`Some invoices failed to send:\n\n${errorMessages}\n\nCheck browser console for more details.`);
    }

    // Clear results after 5 seconds
    setTimeout(() => setSendResults(null), 5000);
  };

  // Invoice All Drafts
  const handleInvoiceAll = async () => {
    if (draftInvoices.length === 0) return;
    
    // Select all drafts first, then send
    const draftIds = new Set(draftInvoices.map(inv => inv.id));
    setSelectedInvoices(draftIds);
    
    // Small delay to show selection, then send
    setTimeout(() => {
      handleSendSelected();
    }, 100);
  };

  // Count selected drafts
  const selectedDraftCount = [...selectedInvoices].filter(id =>
    draftInvoices.some(inv => inv.id === id)
  ).length;

  // Get outstanding invoices (sent, pending, overdue) for reminders
  const outstandingInvoices = useMemo(() =>
    invoicesWithStatus
      .filter(inv => inv.displayStatus === 'sent' || inv.displayStatus === 'pending' || inv.displayStatus === 'overdue')
      .sort((a, b) => {
        // Overdue first, then by due date ascending
        if (a.displayStatus === 'overdue' && b.displayStatus !== 'overdue') return -1;
        if (b.displayStatus === 'overdue' && a.displayStatus !== 'overdue') return 1;
        return new Date(a.dueDate) - new Date(b.dueDate);
      }),
    [invoicesWithStatus]
  );

  // Open reminder modal - pre-select all outstanding invoices that have client email
  const openReminderModal = () => {
    const selectableIds = new Set(
      outstandingInvoices
        .filter(inv => {
          const client = state.clients.find(c => c.id === inv.clientId);
          return client?.accountingEmail || client?.email;
        })
        .map(inv => inv.id)
    );
    setReminderSelections(selectableIds);
    setReminderResults(null);
    setShowReminderModal(true);
  };

  // Toggle reminder selection for a single invoice
  const toggleReminderSelection = (invoiceId) => {
    setReminderSelections(prev => {
      const next = new Set(prev);
      if (next.has(invoiceId)) {
        next.delete(invoiceId);
      } else {
        next.add(invoiceId);
      }
      return next;
    });
  };

  // Select/deselect all reminders
  const toggleAllReminders = () => {
    if (reminderSelections.size === outstandingInvoices.filter(inv => {
      const client = state.clients.find(c => c.id === inv.clientId);
      return client?.accountingEmail || client?.email;
    }).length) {
      setReminderSelections(new Set());
    } else {
      const allIds = new Set(
        outstandingInvoices
          .filter(inv => {
            const client = state.clients.find(c => c.id === inv.clientId);
            return client?.accountingEmail || client?.email;
          })
          .map(inv => inv.id)
      );
      setReminderSelections(allIds);
    }
  };

  // Send batch reminders
  const handleSendReminders = async () => {
    if (reminderSelections.size === 0) return;

    setIsSendingReminders(true);
    setReminderProgress({ current: 0, total: reminderSelections.size });

    const results = { success: [], failed: [], skipped: [] };
    const invoicesToRemind = outstandingInvoices.filter(inv => reminderSelections.has(inv.id));

    for (let i = 0; i < invoicesToRemind.length; i++) {
      const invoice = invoicesToRemind[i];
      setReminderProgress({ current: i + 1, total: invoicesToRemind.length });

      const client = state.clients.find(c => c.id === invoice.clientId);
      const reminderEmail = client?.accountingEmail || client?.email;
      if (!reminderEmail) {
        results.skipped.push({ invoice, reason: 'No email on file' });
        continue;
      }

      try {
        const isOverdue = invoice.displayStatus === 'overdue';
        const daysOver = getDaysOverdue(invoice);
        // Use direct pay link if token exists, otherwise fallback to portal
        const paymentToken = invoice.paymentToken;
        const portalLink = paymentToken
          ? `${window.location.origin}/pay/${paymentToken}`
          : `${window.location.origin}/portal?tab=invoices`;

        const batchLogId = `log_${Date.now()}_${invoice.id}`;
        const reminderResult = await sendEmail('payment_reminder', {
          clientId: client.id,
          commLogId: batchLogId,
          clientEmail: reminderEmail,
          clientName: invoice.clientName,
          invoiceNumber: invoice.invoiceNumber,
          amount: formatCurrency(invoice.balance || invoice.total),
          dueDate: format(parseISO(invoice.dueDate), 'MMMM d, yyyy'),
          portalLink,
          isOverdue,
          daysOverdue: daysOver
        });

        if (!reminderResult.success) {
          throw new Error(reminderResult.error || 'Failed to send reminder email via SendGrid');
        }

        // Update invoice with reminder tracking
        await updateInvoice(invoice.id, {
          lastReminderSent: new Date().toISOString(),
          reminderCount: (invoice.reminderCount || 0) + 1
        });

        // Log communication
        if (client.id) {
          await logClientCommunication(client.id, {
            id: batchLogId,
            type: 'email',
            category: 'invoice_reminder',
            subject: `Payment Reminder - Invoice #${invoice.invoiceNumber}`,
            recipient: reminderEmail,
            status: 'sent',
            details: { invoiceId: invoice.id, invoiceNumber: invoice.invoiceNumber, batchReminder: true }
          });
        }

        results.success.push(invoice);
      } catch (error) {
        console.error(`Failed to send reminder for ${invoice.invoiceNumber}:`, error);
        results.failed.push({ invoice, error: error.message });
      }
    }

    setIsSendingReminders(false);
    setReminderResults(results);
    setReminderSelections(new Set());
  };

  // Batch Stripe sync — checks invoices against Stripe to:
  //  1. Mark unpaid invoices as paid if Stripe shows them paid.
  //  2. Backfill stripePaidAt (the actual Stripe payment date) on already-paid
  //     invoices that don't have it yet, so we can see the date here without
  //     opening Stripe.
  // Invoices without a stripeInvoiceId are looked up by metadata.internalInvoiceId,
  // since Stripe invoices are usually only created when the client opens the
  // payment portal — which means they may exist in Stripe even when the link
  // was never persisted locally.
  const handleSyncStripe = async () => {
    // Outstanding invoices that may have been paid in Stripe.
    const outstanding = invoicesWithStatus.filter(inv =>
      inv.displayStatus === 'sent' || inv.displayStatus === 'pending' || inv.displayStatus === 'overdue'
    );
    // Already-paid invoices missing the Stripe paid date — eligible for backfill.
    const backfillable = invoicesWithStatus.filter(inv =>
      inv.displayStatus === 'paid'
      && !inv.stripePaidAt
      && (inv.stripeInvoiceId || inv.paymentMethod === 'stripe_invoice' || inv.paymentMethod === 'stripe' || inv.paymentMethod === 'stripe_checkout' || inv.paymentMethod === 'stripe_charge')
    );

    const syncable = [...outstanding, ...backfillable];

    if (syncable.length === 0) {
      alert('Nothing to sync — no outstanding invoices and all paid invoices already have a Stripe paid date.');
      return;
    }

    const message = backfillable.length > 0
      ? `Sync ${syncable.length} invoice(s) with Stripe?\n\n• ${outstanding.length} outstanding (check for payment)\n• ${backfillable.length} paid (backfill Stripe paid date)`
      : `Check ${outstanding.length} outstanding invoice(s) against Stripe for payment updates?`;

    if (!window.confirm(message)) return;

    setIsSyncingStripe(true);
    setStripeSyncResults(null);
    setStripeSyncProgress({ current: 0, total: syncable.length });

    const results = { synced: [], backfilled: [], unchanged: [], notInStripe: [], failed: [] };

    for (let i = 0; i < syncable.length; i++) {
      const invoice = syncable[i];
      setStripeSyncProgress({ current: i + 1, total: syncable.length });

      try {
        let stripeInvoiceId = invoice.stripeInvoiceId;
        let stripeInvoice;

        if (stripeInvoiceId) {
          const result = await getStripeInvoice(stripeInvoiceId);
          stripeInvoice = result.invoice;
        } else {
          const lookup = await findStripeInvoiceByInternalId(invoice.id);
          if (!lookup.found) {
            results.notInStripe.push(invoice);
            continue;
          }
          stripeInvoice = lookup.invoice;
          stripeInvoiceId = stripeInvoice.id;
          // Re-link the orphaned Stripe invoice to our local record so future
          // syncs are direct and the portal can finalize tip flows.
          try {
            await updateInvoice(invoice.id, {
              stripeInvoiceId,
              stripePaymentUrl: stripeInvoice.hosted_invoice_url || null
            });
          } catch (e) {
            console.error('Failed to relink stripeInvoiceId:', e);
          }
        }

        if (stripeInvoice.status === 'paid') {
          const stripePaidAt = stripeInvoice.status_transitions?.paid_at
            ? new Date(stripeInvoice.status_transitions.paid_at * 1000).toISOString()
            : null;

          // Backfill-only path: invoice is already paid locally, just need the date.
          if (invoice.displayStatus === 'paid') {
            if (stripePaidAt && stripePaidAt !== invoice.stripePaidAt) {
              await updateInvoice(invoice.id, { stripePaidAt });
              if (invoice.jobId) {
                try {
                  await updateJob(invoice.jobId, { invoiceStripePaidAt: stripePaidAt });
                } catch (e) {
                  console.error('Failed to update job:', e);
                }
              }
              results.backfilled.push(invoice);
            } else {
              results.unchanged.push(invoice);
            }
            continue;
          }

          // Full sync path: mark as paid.
          let tipAmount = 0;
          const lines = stripeInvoice.lines?.data || [];
          for (const line of lines) {
            if ((line.description || '').toLowerCase().includes('tip')) {
              tipAmount = (line.amount || 0) / 100;
            }
          }
          const amountPaid = stripeInvoice.amount_paid / 100;
          const paidAt = stripePaidAt || new Date().toISOString();

          await updateInvoice(invoice.id, {
            status: 'paid',
            paidAt,
            stripePaidAt: stripePaidAt || paidAt,
            amountPaid,
            tipAmount: tipAmount || invoice.tipAmount || 0,
            balance: 0,
            paymentMethod: 'stripe_invoice',
            syncedFromManualCheck: true
          });

          if (invoice.jobId) {
            try {
              await updateJob(invoice.jobId, {
                invoicePaid: true,
                invoicePaidAt: paidAt,
                invoiceStripePaidAt: stripePaidAt || paidAt
              });
            } catch (e) {
              console.error('Failed to update job:', e);
            }
          }

          results.synced.push(invoice);
        } else {
          results.unchanged.push(invoice);
        }
      } catch (err) {
        console.error(`Stripe check failed for ${invoice.invoiceNumber}:`, err);
        results.failed.push(invoice);
      }
    }

    setIsSyncingStripe(false);
    setStripeSyncResults(results);
  };

  return (
    <div className="invoices-page">
      {/* Header */}
      <div className="invoices-header">
        <div className="header-title">
          <h1>Invoices</h1>
          <p className="subtitle">Manage billing and payments</p>
        </div>
        <div className="header-actions">
          <button
            className="btn-secondary"
            onClick={handleSyncStripe}
            disabled={isSyncingStripe || isSending}
          >
            {isSyncingStripe ? (
              <>
                <Loader size={18} className="spin" />
                Syncing {stripeSyncProgress.current}/{stripeSyncProgress.total}...
              </>
            ) : (
              <>
                <CreditCard size={18} />
                Sync Stripe
              </>
            )}
          </button>
          {outstandingInvoices.length > 0 && (
            <button
              className="btn-send-reminders"
              onClick={openReminderModal}
              disabled={isSending || isSendingReminders}
            >
              <Bell size={18} />
              Send Reminders ({outstandingInvoices.length})
            </button>
          )}
          {stats.draft > 0 && (
            <button
              className="btn-invoice-all"
              onClick={handleInvoiceAll}
              disabled={isSending}
            >
              <Send size={18} />
              Invoice All ({stats.draft})
            </button>
          )}
          <Link to="/invoices/new" className="btn-primary">
            <Plus size={18} />
            Create Invoice
          </Link>
        </div>
      </div>

      {/* Send Results Banner */}
      {sendResults && (
        <div className={`send-results-banner ${sendResults.failed.length > 0 ? 'has-errors' : ''}`}>
          {sendResults.success.length > 0 && (
            <span className="success-count">
              <CheckCircle size={16} />
              {sendResults.success.length} invoice(s) sent successfully
            </span>
          )}
          {sendResults.failed.length > 0 && (
            <span className="failed-count">
              <AlertCircle size={16} />
              {sendResults.failed.length} failed to send
            </span>
          )}
          <button className="close-banner" onClick={() => setSendResults(null)}>×</button>
        </div>
      )}

      {/* Stripe Sync Results Banner */}
      {stripeSyncResults && (
        <div className={`send-results-banner ${stripeSyncResults.failed.length > 0 ? 'has-errors' : ''}`}>
          {stripeSyncResults.synced.length > 0 && (
            <span className="success-count">
              <CheckCircle size={16} />
              {stripeSyncResults.synced.length} invoice(s) synced as paid
            </span>
          )}
          {stripeSyncResults.backfilled?.length > 0 && (
            <span className="success-count">
              <Calendar size={16} />
              {stripeSyncResults.backfilled.length} paid date(s) backfilled
            </span>
          )}
          {stripeSyncResults.unchanged.length > 0 && (
            <span>
              <Clock size={16} />
              {stripeSyncResults.unchanged.length} already up to date
            </span>
          )}
          {stripeSyncResults.notInStripe?.length > 0 && (
            <span>
              <AlertCircle size={16} />
              {stripeSyncResults.notInStripe.length} not in Stripe yet
            </span>
          )}
          {stripeSyncResults.failed.length > 0 && (
            <span className="failed-count">
              <AlertCircle size={16} />
              {stripeSyncResults.failed.length} failed to check
            </span>
          )}
          <button className="close-banner" onClick={() => setStripeSyncResults(null)}>×</button>
        </div>
      )}

      {/* Selection Actions Bar */}
      {selectedInvoices.size > 0 && (
        <div className="selection-bar">
          <span className="selection-count">
            <CheckSquare size={18} />
            {selectedInvoices.size} invoice(s) selected
          </span>
          <div className="selection-actions">
            <button className="btn-ghost" onClick={clearSelection}>
              Clear Selection
            </button>
            {selectedDraftCount > 0 && (
              <button 
                className="btn-send-selected"
                onClick={handleSendSelected}
                disabled={isSending}
              >
                {isSending ? (
                  <>
                    <Loader size={16} className="spin" />
                    Sending {sendingProgress.current}/{sendingProgress.total}...
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    Send {selectedDraftCount} Invoice(s)
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="stats-row">
        <div
          className={`stat-card clickable ${statusFilter === 'draft' ? 'active' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'draft' ? 'all' : 'draft')}
        >
          <div className="stat-icon gray">
            <FileText size={20} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{stats.draft}</span>
            <span className="stat-label">Drafts Ready</span>
            {stats.draft > 0 && (
              <span className="stat-sublabel">{formatCurrency(stats.draftTotal)} to invoice</span>
            )}
          </div>
        </div>

        <div
          className={`stat-card clickable ${statusFilter === 'pending' ? 'active' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'pending' ? 'all' : 'pending')}
        >
          <div className="stat-icon orange">
            <Clock size={20} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{stats.pending}</span>
            <span className="stat-label">Awaiting Payment</span>
            {stats.pending > 0 && (
              <span className="stat-sublabel">{formatCurrency(stats.pendingTotal)} pending</span>
            )}
          </div>
        </div>

        <div
          className={`stat-card warning clickable ${statusFilter === 'overdue' ? 'active' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'overdue' ? 'all' : 'overdue')}
        >
          <div className="stat-icon red">
            <AlertCircle size={20} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{formatCurrency(stats.overdueTotal)}</span>
            <span className="stat-label">Overdue</span>
            <span className="stat-sublabel">
              {stats.overdue} invoice{stats.overdue === 1 ? '' : 's'} past due
            </span>
          </div>
        </div>

        <div
          className={`stat-card clickable ${statusFilter === 'paid' ? 'active' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'paid' ? 'all' : 'paid')}
        >
          <div className="stat-icon green">
            <DollarSign size={20} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{formatCurrency(stats.totalPaidThisMonth)}</span>
            <span className="stat-label">Paid This Month</span>
            {stats.paid > 0 && (
              <span className="stat-sublabel">
                {stats.paid} invoice{stats.paid === 1 ? '' : 's'} total
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search invoices..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="filter-tabs">
          <button
            className={`filter-tab ${statusFilter === 'all' ? 'active' : ''}`}
            onClick={() => setStatusFilter('all')}
          >
            All
          </button>
          <button
            className={`filter-tab ${statusFilter === 'draft' ? 'active' : ''}`}
            onClick={() => setStatusFilter('draft')}
          >
            Draft ({stats.draft})
          </button>
          <button
            className={`filter-tab ${statusFilter === 'pending' ? 'active' : ''}`}
            onClick={() => setStatusFilter('pending')}
          >
            Awaiting Payment ({stats.pending})
          </button>
          <button
            className={`filter-tab ${statusFilter === 'overdue' ? 'active' : ''}`}
            onClick={() => setStatusFilter('overdue')}
          >
            Overdue ({stats.overdue})
          </button>
          <button
            className={`filter-tab ${statusFilter === 'paid' ? 'active' : ''}`}
            onClick={() => setStatusFilter('paid')}
          >
            Paid ({stats.paid})
          </button>
          {stats.depositsPending > 0 && (
            <button
              className={`filter-tab deposit-tab ${statusFilter === 'deposit_pending' ? 'active' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'deposit_pending' ? 'all' : 'deposit_pending')}
            >
              <CreditCard size={14} />
              Deposits Due ({stats.depositsPending})
            </button>
          )}
        </div>

        <div className="filter-selects">
          <select
            className="date-filter"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
          >
            <option value="all">All Time</option>
            <option value="this_month">This Month</option>
            <option value="last_month">Last Month</option>
          </select>

          <select
            className="date-filter sort-filter"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            title="Sort invoices"
          >
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="due_soon">Due soon</option>
            <option value="amount_high">Amount (high → low)</option>
            <option value="amount_low">Amount (low → high)</option>
            <option value="client">Client A → Z</option>
          </select>
        </div>
      </div>

      {/* Draft Selection Helper */}
      {statusFilter === 'draft' && draftInvoices.length > 0 && (
        <div className="draft-helper">
          <button className="btn-select-all" onClick={selectAllDrafts}>
            <CheckSquare size={16} />
            Select All Drafts
          </button>
          <span className="helper-text">
            Select invoices to send, or click "Invoice All" to send all drafts at once
          </span>
        </div>
      )}

      {/* Invoice List */}
      <div className="invoices-list">
        {filteredInvoices.length === 0 ? (
          <div className="empty-state">
            <FileText size={48} />
            <h3>No invoices found</h3>
            <p>
              {searchQuery || statusFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'Invoices are automatically created when jobs are completed for clients with auto-invoicing enabled'}
            </p>
          </div>
        ) : (
          filteredInvoices.map(invoice => {
            const statusConfig = invoiceStatuses[invoice.displayStatus] || invoiceStatuses.draft;
            const isSelected = selectedInvoices.has(invoice.id);
            const isDraft = invoice.status === 'draft';
            const isOverdue = invoice.displayStatus === 'overdue';
            const daysOver = isOverdue ? getDaysOverdue(invoice) : 0;
            const reminderDaysAgo = invoice.lastReminderSent
              ? Math.floor((Date.now() - new Date(invoice.lastReminderSent).getTime()) / (1000 * 60 * 60 * 24))
              : null;

            return (
              <div
                key={invoice.id}
                className={`invoice-card status-${invoice.displayStatus} ${isSelected ? 'selected' : ''} ${isOverdue ? 'is-overdue' : ''}`}
                onClick={() => navigate(`/invoices/${invoice.id}`)}
              >
                {/* Checkbox for drafts */}
                {isDraft && (
                  <div 
                    className="invoice-checkbox"
                    onClick={(e) => toggleSelection(invoice.id, e)}
                  >
                    {isSelected ? (
                      <CheckSquare size={20} className="checked" />
                    ) : (
                      <Square size={20} />
                    )}
                  </div>
                )}

                <div className="invoice-main">
                  <div className="invoice-number">
                    <span className="number">{invoice.invoiceNumber}</span>
                    <span
                      className="status-badge"
                      style={{
                        background: statusConfig.bg,
                        color: statusConfig.color
                      }}
                    >
                      {getStatusIcon(invoice.displayStatus)}
                      {statusConfig.label}
                    </span>
                    {isOverdue && daysOver > 0 && (
                      <span className="overdue-pill" title={`${daysOver} days past due`}>
                        <AlertCircle size={12} />
                        {daysOver}d overdue
                      </span>
                    )}
                    {reminderDaysAgo !== null && !isDraft && invoice.displayStatus !== 'paid' && (
                      <span
                        className="reminder-pill"
                        title={`Last reminder ${format(parseISO(invoice.lastReminderSent), 'MMM d, yyyy')}${invoice.reminderCount > 1 ? ` · ${invoice.reminderCount} reminders total` : ''}`}
                      >
                        <Bell size={11} />
                        {reminderDaysAgo === 0 ? 'Reminded today' : `Reminded ${reminderDaysAgo}d ago`}
                      </span>
                    )}
                  </div>

                  <div className="invoice-client">
                    <User size={14} />
                    <span>{invoice.clientName}</span>
                  </div>

                  <div className="invoice-property">
                    <Home size={14} />
                    <span>{invoice.propertyAddress}</span>
                  </div>
                </div>

                <div className="invoice-dates">
                  <div className="date-item">
                    <span className="date-label">Invoice Date</span>
                    <span className="date-value">
                      {format(parseISO(invoice.invoiceDate), 'MMM d, yyyy')}
                    </span>
                  </div>
                  <div className="date-item">
                    <span className="date-label">Due Date</span>
                    <span className={`date-value ${invoice.displayStatus === 'overdue' ? 'overdue' : ''}`}>
                      {format(parseISO(invoice.dueDate), 'MMM d, yyyy')}
                    </span>
                  </div>
                  {invoice.sentAt && (
                    <div className="date-item sent-indicator">
                      <span className="date-label">Sent</span>
                      <span className="date-value sent">
                        {format(parseISO(invoice.sentAt), 'MMM d')}
                        {invoice.sentVia && <small> via {invoice.sentVia}</small>}
                      </span>
                    </div>
                  )}
                  {invoice.displayStatus === 'paid' && (invoice.stripePaidAt || invoice.paidAt) && (
                    <div className="date-item paid-indicator">
                      <span className="date-label">
                        {invoice.stripePaidAt ? 'Paid (Stripe)' : 'Paid'}
                      </span>
                      <span className="date-value paid">
                        {format(parseISO(invoice.stripePaidAt || invoice.paidAt), 'MMM d, yyyy')}
                      </span>
                    </div>
                  )}
                </div>

                <div className="invoice-amount">
                  <span className="amount-label">Total</span>
                  <span className="amount-value">{formatCurrency(invoice.total)}</span>
                  {invoice.balance > 0 && invoice.balance < invoice.total && (
                    <span className="amount-balance">
                      Balance: {formatCurrency(invoice.balance)}
                    </span>
                  )}
                  {invoice.tipAmount > 0 && (
                    <span className="tip-badge" title={`Tip included: ${formatCurrency(invoice.tipAmount)}`}>
                      <Heart size={12} />
                      Tip: {formatCurrency(invoice.tipAmount)}
                    </span>
                  )}
                  {invoice.requestDeposit && (
                    <span className={`deposit-badge ${invoice.depositPaid ? 'paid' : 'pending'}`}>
                      <CreditCard size={12} />
                      {invoice.depositPaid
                        ? `Deposit Paid`
                        : `Deposit Due: ${formatCurrency(invoice.depositAmount)}`
                      }
                    </span>
                  )}
                </div>

                <div className="invoice-actions">
                  {isDraft ? (
                    <button 
                      className="btn-send-single"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedInvoices(new Set([invoice.id]));
                        setTimeout(() => handleSendSelected(), 100);
                      }}
                      disabled={isSending}
                    >
                      <Send size={16} />
                    </button>
                  ) : invoice.displayStatus === 'sent' || invoice.displayStatus === 'pending' || invoice.displayStatus === 'overdue' ? (
                    <button 
                      className="btn-mark-paid"
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (window.confirm(`Mark invoice #${invoice.invoiceNumber} as PAID?`)) {
                          try {
                            await updateInvoice(invoice.id, {
                              status: 'paid',
                              paidAt: new Date().toISOString(),
                              amountPaid: invoice.balance || invoice.total,
                              balance: 0,
                              paymentMethod: 'manual',
                              manuallyMarkedPaid: true
                            });
                          } catch (err) {
                            console.error('Failed:', err);
                            alert('Failed to mark as paid');
                          }
                        }
                      }}
                      title="Mark as Paid"
                    >
                      <Check size={16} />
                    </button>
                  ) : (
                    <ChevronRight size={20} />
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Batch Reminder Modal */}
      {showReminderModal && (
        <div className="modal-overlay" onClick={() => !isSendingReminders && setShowReminderModal(false)}>
          <div className="reminder-modal" onClick={e => e.stopPropagation()}>
            <div className="reminder-modal-header">
              <div>
                <h2>Send Invoice Reminders</h2>
                <p className="reminder-subtitle">
                  Select which clients to send payment reminder emails to
                </p>
              </div>
              <button
                className="reminder-close-btn"
                onClick={() => !isSendingReminders && setShowReminderModal(false)}
              >
                <X size={20} />
              </button>
            </div>

            {/* Results banner inside modal */}
            {reminderResults && (
              <div className={`reminder-results ${reminderResults.failed.length > 0 ? 'has-errors' : ''}`}>
                {reminderResults.success.length > 0 && (
                  <span className="success-count">
                    <CheckCircle size={16} />
                    {reminderResults.success.length} reminder(s) sent
                  </span>
                )}
                {reminderResults.failed.length > 0 && (
                  <span className="failed-count">
                    <AlertCircle size={16} />
                    {reminderResults.failed.length} failed
                  </span>
                )}
                {reminderResults.skipped.length > 0 && (
                  <span className="skipped-count">
                    {reminderResults.skipped.length} skipped (no email)
                  </span>
                )}
              </div>
            )}

            <div className="reminder-modal-body">
              {outstandingInvoices.length === 0 ? (
                <div className="reminder-empty">
                  <CheckCircle size={40} />
                  <p>No outstanding invoices to send reminders for.</p>
                </div>
              ) : (
                <>
                  {/* Select all toggle */}
                  <div className="reminder-select-all">
                    <button className="btn-toggle-all" onClick={toggleAllReminders}>
                      {reminderSelections.size === outstandingInvoices.filter(inv => {
                        const client = state.clients.find(c => c.id === inv.clientId);
                        return client?.accountingEmail || client?.email;
                      }).length ? (
                        <CheckSquare size={18} className="checked" />
                      ) : (
                        <Square size={18} />
                      )}
                      Select All
                    </button>
                    <span className="reminder-selection-info">
                      {reminderSelections.size} of {outstandingInvoices.length} selected
                    </span>
                  </div>

                  {/* Invoice list */}
                  <div className="reminder-invoice-list">
                    {outstandingInvoices.map(invoice => {
                      const client = state.clients.find(c => c.id === invoice.clientId);
                      const hasEmail = !!(client?.accountingEmail || client?.email);
                      const isSelected = reminderSelections.has(invoice.id);
                      const isOverdue = invoice.displayStatus === 'overdue';
                      const daysOver = getDaysOverdue(invoice);
                      const statusConfig = invoiceStatuses[invoice.displayStatus] || invoiceStatuses.sent;

                      return (
                        <div
                          key={invoice.id}
                          className={`reminder-invoice-row ${isSelected ? 'selected' : ''} ${!hasEmail ? 'no-email' : ''}`}
                          onClick={() => hasEmail && toggleReminderSelection(invoice.id)}
                        >
                          <div className="reminder-checkbox">
                            {hasEmail ? (
                              isSelected ? (
                                <CheckSquare size={20} className="checked" />
                              ) : (
                                <Square size={20} />
                              )
                            ) : (
                              <Square size={20} className="disabled" />
                            )}
                          </div>

                          <div className="reminder-invoice-info">
                            <div className="reminder-invoice-top">
                              <span className="reminder-invoice-number">{invoice.invoiceNumber}</span>
                              <span
                                className="status-badge"
                                style={{ background: statusConfig.bg, color: statusConfig.color }}
                              >
                                {isOverdue ? <AlertCircle size={12} /> : <Clock size={12} />}
                                {statusConfig.label}
                                {isOverdue && daysOver > 0 && ` (${daysOver}d)`}
                              </span>
                            </div>
                            <div className="reminder-invoice-client">
                              <User size={13} />
                              {invoice.clientName}
                            </div>
                            {!hasEmail && (
                              <span className="reminder-no-email-tag">No email on file</span>
                            )}
                            {hasEmail && invoice.lastReminderSent && (
                              <span className="reminder-last-sent">
                                Last reminder: {format(parseISO(invoice.lastReminderSent), 'MMM d')}
                                {invoice.reminderCount > 1 && ` (${invoice.reminderCount}x)`}
                              </span>
                            )}
                          </div>

                          <div className="reminder-invoice-amount">
                            <span className="reminder-amount-value">
                              {formatCurrency(invoice.balance || invoice.total)}
                            </span>
                            <span className="reminder-due-date">
                              Due {format(parseISO(invoice.dueDate), 'MMM d')}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            <div className="reminder-modal-footer">
              <button
                className="btn-ghost"
                onClick={() => setShowReminderModal(false)}
                disabled={isSendingReminders}
              >
                {reminderResults ? 'Close' : 'Cancel'}
              </button>
              {!reminderResults && (
                <button
                  className="btn-send-reminders-confirm"
                  onClick={handleSendReminders}
                  disabled={reminderSelections.size === 0 || isSendingReminders}
                >
                  {isSendingReminders ? (
                    <>
                      <Loader size={16} className="spin" />
                      Sending {reminderProgress.current}/{reminderProgress.total}...
                    </>
                  ) : (
                    <>
                      <Mail size={16} />
                      Send {reminderSelections.size} Reminder{reminderSelections.size !== 1 ? 's' : ''}
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Invoices;
